//Import Modules
import * as THREE from 'three';

/* Cursor-Parallax fuer die 3D-Fruechte -----------------------------------------------------------
 *
 * Laesst ein geladenes Objekt der Cursor-Position folgen. Anders als bei den Dosen:
 *   - die Drehung ist ein OFFSET auf die Grundausrichtung (rotation.y += 1.9 bleibt erhalten)
 *   - position wird nicht angefasst (die Fruechte sind auf feste Werte eingemessen)
 *   - gerechnet wird pro Frame in der animate()-Schleife, nicht im mousemove-Handler
 *
 * Gedreht wird ueber Quaternionen um zwei feste WELT-Achsen:
 *   - Gieren  um die Y-Achse (Frucht dreht sich wie auf einem Plattenteller)
 *   - Nicken  um die "rechts"-Achse der Kamera (Frucht kippt zum Betrachter hin und weg)
 * Die Kippachse kommt aus der Kamera, weil jede Frucht aus einem anderen Winkel gefilmt wird.
 * Ueber rotation.x waere es die Welt-X-Achse - die zeigt z.B. beim Rhabarber fast direkt in
 * die Blickrichtung, was als seitliches Rollen statt als Nicken ankommt.
 *
 * Die Staerke wird als Gesamtschwenk in GRAD ueber die volle Fensterbreite bzw. -hoehe
 * angegeben und ist damit unabhaengig von der Bildschirmaufloesung.
 * (Zum Vergleich: die Dosen drehen je nach Monitorbreite zwischen 495 und 880 Grad.)
 * -------------------------------------------------------------------------------------------------*/

//Mobile Device Check
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const WORLD_UP = new THREE.Vector3(0, 1, 0);

/* Cursor-Position, normalisiert auf -0.5 .. +0.5 relativ zur Fenstermitte.
   Ein einziger Listener pro Seite, egal wie viele Fruechte ihn nutzen. */
var pointer = { x: 0, y: 0 };
var listening = false;

function startListening(){
    if(listening || isMobile) return;
    listening = true;
    document.addEventListener('mousemove', function(event){
        pointer.x = (event.clientX - window.innerWidth  / 2) / window.innerWidth;
        pointer.y = (event.clientY - window.innerHeight / 2) / window.innerHeight;
    });
}

/**
 * @param {THREE.Object3D} obj      das geladene Modell (gltf.scene)
 * @param {THREE.Camera}   camera   die Kamera der Szene - liefert die Kippachse
 * @param {Object} [options]
 * @param {number} [options.swingX] Gesamtschwenk links<->rechts in Grad (Default 55)
 * @param {number} [options.swingY] Gesamtschwenk oben<->unten in Grad  (Default 30,
 *                                  negativer Wert kippt in die Gegenrichtung)
 * @param {number} [options.ease]   Traegheit, 0..1 - kleiner = weicher (Default 0.08)
 * @param {number} [options.tilt]   feste Grundneigung in Grad (Default 0). Positiv kippt die
 *                                  Oberseite ZUM Betrachter hin, negativ von ihm weg. Wirkt um
 *                                  dieselbe Achse wie der Cursor-Effekt, also auf jeder Seite
 *                                  gleich - anders als rotation.x, das je nach Kamera rollt.
 * @returns {Function} pro Frame aufrufen; auf Mobile eine leere Funktion
 */
function followPointer(obj, camera, options){
    var opts   = options || {};
    var swingX = (opts.swingX !== undefined ? opts.swingX : 55) * Math.PI / 180;
    var swingY = (opts.swingY !== undefined ? opts.swingY : 30) * Math.PI / 180;
    var ease   =  opts.ease   !== undefined ? opts.ease   : 0.08;
    var tilt   = (opts.tilt   !== undefined ? opts.tilt   : 0)  * Math.PI / 180;

    //camera ist Pflicht - ohne sie gibt es keine verlaessliche Kippachse
    if(isMobile || !obj || !camera || !camera.isCamera) return function(){};
    startListening();

    //Grundausrichtung und -position merken, damit die Cursor-Drehung nur als Offset wirkt
    var baseQuat = obj.quaternion.clone();
    var basePos  = obj.position.clone();

    /* Kippachse = "rechts" im Bild. Spalte 0 der Kamera-Weltmatrix ist deren X-Achse.
       Die Kameras stehen fest (controls.enableRotate = false), einmal auslesen genuegt. */
    camera.updateMatrixWorld();
    var rightAxis = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();

    /* Drehzentrum = Mitte der Bounding-Box. Die Blender-Exporte haben ihren Ursprung
       am Fuss des Modells; ohne Korrektur wuerde die Frucht um ihre Unterkante schwenken
       wie ein Pendel, statt sich auf der Stelle zu neigen. */
    obj.updateWorldMatrix(true, true);
    var pivot = new THREE.Box3().setFromObject(obj).getCenter(new THREE.Vector3());

    /* Feste Grundneigung um dieselbe Achse wie der Cursor-Effekt - und um die Mitte,
       damit die Frucht auf der Stelle kippt statt aus dem Bild zu wandern. */
    if(tilt !== 0){
        var qTilt = new THREE.Quaternion().setFromAxisAngle(rightAxis, tilt);
        basePos.sub(pivot).applyQuaternion(qTilt).add(pivot);
        baseQuat.premultiply(qTilt);
        obj.position.copy(basePos);
        obj.quaternion.copy(baseQuat);
        obj.updateWorldMatrix(true, true);
        new THREE.Box3().setFromObject(obj).getCenter(pivot);
    }

    var pivotOffset = new THREE.Vector3().subVectors(basePos, pivot);

    var qYaw   = new THREE.Quaternion();
    var qPitch = new THREE.Quaternion();
    var qSpin  = new THREE.Quaternion();
    var yaw = 0, pitch = 0;

    return function(){
        //weiches Nachlaufen pro Frame
        yaw   += (pointer.x * swingX - yaw)   * ease;
        pitch += (pointer.y * swingY - pitch) * ease;

        qYaw.setFromAxisAngle(WORLD_UP, yaw);
        qPitch.setFromAxisAngle(rightAxis, pitch);
        qSpin.copy(qYaw).premultiply(qPitch);   //erst Gieren, dann Nicken - beides in Weltachsen

        //Ausrichtung: Grunddrehung, dann die Cursor-Drehung obendrauf
        obj.quaternion.copy(baseQuat).premultiply(qSpin);

        //Position mitdrehen, damit sich das Modell um seine Mitte statt um seinen Fuss neigt
        obj.position.copy(pivotOffset).applyQuaternion(qSpin).add(pivot);
    };
}

export { followPointer };
